if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');

      this.sliderOptions = {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: swiperOptions.slidesPerViewMobile || 'auto',
        resistanceRatio: 0.72,

        /*
         * IMPORTANT:
         * Do NOT use Swiper mousewheel here.
         *
         * We handle macOS trackpad wheel manually below.
         */

        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween:
              swiperOptions.spaceBetweenDesktop || 2
          },

          1100: {
            slidesPerView: 4,
            spaceBetween:
              swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };

      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }

      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }

      if (swiperOptions.loop) {
        this.sliderOptions.loop = swiperOptions.loop;
      }

      /*
       * Trackpad values
       */
      this.wheelStartX = 0;
      this.wheelStartY = 0;
      this.wheelLocked = false;
      this.wheelDeltaX = 0;

      this.handleTrackpadWheel =
        this.handleTrackpadWheel.bind(this);

      this._resizeHandler = () => {
        this.syncHeights();
      };
    }

    connectedCallback() {
      /*
       * Allow vertical page scrolling.
       *
       * IMPORTANT:
       * Do not use touch-action: none.
       */
      this.style.touchAction = 'pan-y';

      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }

      this.initSlider();
      this.syncHeights();

      window.addEventListener(
        'resize',
        this._resizeHandler
      );
    }

    disconnectedCallback() {
      window.removeEventListener(
        'resize',
        this._resizeHandler
      );

      this.removeEventListener(
        'wheel',
        this.handleTrackpadWheel
      );

      if (
        this.slider &&
        !this.slider.destroyed
      ) {
        this.slider.destroy();
      }
    }

    syncHeights() {
      const textSlide = this.querySelector(
        '.products-feed__card-text-custom-slide'
      );

      const actionElement = this.querySelector(
        '.product-card__actions'
      );

      if (textSlide && actionElement) {
        const actionHeight =
          actionElement.offsetHeight;

        textSlide.style.height =
          `${actionHeight}px`;
      }
    }

    fetchItems() {
      if (!this.parent) {
        throw new Error('Parent is missing');
      }

      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html =
            new DOMParser().parseFromString(
              text,
              'text/html'
            );

          const wrapper =
            html.querySelector('.swiper-wrapper');

          if (
            wrapper &&
            wrapper.innerHTML.trim().length
          ) {
            this.querySelector(
              '.swiper-wrapper'
            ).innerHTML =
              wrapper.innerHTML;

            this.parent.classList.remove(
              'hidden'
            );

            this.initSlider();
            this.syncHeights();
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    /*
     * =====================================================
     * MACOS TRACKPAD
     * =====================================================
     *
     * TWO FINGER UP/DOWN
     * ------------------
     * Vertical gesture
     * -> NORMAL PAGE SCROLL
     *
     *
     * TWO FINGER LEFT/RIGHT
     * ---------------------
     * Horizontal gesture
     * -> SWIPER
     *
     *
     * VERY IMPORTANT:
     * We don't let a small deltaX from a vertical gesture
     * trigger the slider.
     */
    handleTrackpadWheel(e) {
      if (
        !this.slider ||
        this.slider.destroyed
      ) {
        return;
      }

      const x = e.deltaX;
      const y = e.deltaY;

      const absX = Math.abs(x);
      const absY = Math.abs(y);

      /*
       * Ignore extremely small trackpad noise.
       */
      if (
        absX < 1 &&
        absY < 1
      ) {
        return;
      }

      /*
       * ==================================================
       * VERTICAL SCROLL
       * ==================================================
       *
       * If vertical movement is greater than horizontal
       * movement, ALWAYS allow browser scrolling.
       *
       * MOST IMPORTANT PART:
       *
       * We DO NOT call preventDefault().
       */
      if (absY >= absX) {
        this.wheelLocked = false;
        this.wheelDeltaX = 0;

        return;
      }

      /*
       * ==================================================
       * REQUIRE STRONG HORIZONTAL MOVEMENT
       * ==================================================
       *
       * macOS can send a small deltaX while the user is
       * scrolling vertically.
       *
       * Therefore horizontal must be clearly stronger.
       */
      if (absX <= absY * 1.5) {
        this.wheelLocked = false;
        this.wheelDeltaX = 0;

        return;
      }

      /*
       * ==================================================
       * HORIZONTAL TRACKPAD
       * ==================================================
       */

      e.preventDefault();

      e.stopPropagation();

      this.wheelDeltaX += x;

      /*
       * Wait for enough horizontal movement before
       * changing the slide.
       */
      const slideThreshold = 40;

      if (
        Math.abs(this.wheelDeltaX) <
        slideThreshold
      ) {
        return;
      }

      /*
       * RIGHT
       */
      if (this.wheelDeltaX > 0) {
        this.slider.slideNext();
      }

      /*
       * LEFT
       */
      else {
        this.slider.slidePrev();
      }

      /*
       * Reset after changing slide.
       */
      this.wheelDeltaX = 0;
    }

    initSlider(slider = this) {
      /*
       * Destroy previous Swiper instance.
       */
      if (
        this.slider &&
        !this.slider.destroyed
      ) {
        this.slider.destroy();
      }

      /*
       * Remove previous wheel event.
       */
      this.removeEventListener(
        'wheel',
        this.handleTrackpadWheel
      );

      /*
       * Create Swiper.
       */
      this.slider =
        new Swiper(
          slider,
          this.sliderOptions
        );

      /*
       * =================================================
       * MACOS TRACKPAD WHEEL
       * =================================================
       *
       * passive:false is required only because horizontal
       * gestures use preventDefault().
       *
       * Vertical gestures NEVER use preventDefault().
       */
      this.addEventListener(
        'wheel',
        this.handleTrackpadWheel,
        {
          passive: false
        }
      );

      /*
       * Accessibility
       */
      this.slider.wrapperEl.setAttribute(
        'aria-live',
        'polite'
      );

      this.slider.wrapperEl.id =
        `swiper-wrapper-${this.dataset.sectionId}`;

      this.slider.slides.forEach(
        (slide, i) => {
          slide.setAttribute(
            'role',
            'group'
          );

          slide.setAttribute(
            'aria-label',
            `${i + 1} / ${this.slider.slides.length}`
          );

          slide.dataset.index = i;
        }
      );

      /*
       * Navigation accessibility
       */
      if (
        this.sliderOptions?.navigation
      ) {
        const sliderButtons = [
          this.slider.navigation?.prevEl,
          this.slider.navigation?.nextEl
        ];

        sliderButtons.forEach(
          (button, i) => {
            if (!button) {
              return;
            }

            let type = 'Previous';

            if (i === 1) {
              type = 'Next';
            }

            button.setAttribute(
              'aria-label',
              `${type} slide`
            );

            button.setAttribute(
              'aria-controls',
              this.slider.wrapperEl.id
            );
          }
        );
      }

      /*
       * Focus handling
       */
      this.slider.wrapperEl.addEventListener(
        'focusin',
        e => {
          this.slider.el.scrollLeft = 0;

          const slide =
            e.target.closest(
              '.swiper-slide'
            );

          if (!slide) {
            return;
          }

          const slideIndex =
            +slide.dataset.index;

          const isFirstSlide =
            slideIndex === 0;

          const isLastSlide =
            slideIndex ===
            this.slider.slides.length - 1;

          if (
            isFirstSlide ||
            isLastSlide
          ) {
            this.slider.slideTo(
              slideIndex,
              0
            );

            return;
          }

          const slideRect =
            slide.getBoundingClientRect();

          const wrapperWidth =
            this.slider.wrapperEl
              .clientWidth;

          const slideLeft =
            slideRect.x +
            slideRect.width -
            (
              (
                document.body.clientWidth -
                wrapperWidth
              ) / 2
            );

          const isSlideVisible =
            slideLeft <= wrapperWidth &&
            slideLeft >= slideRect.width;

          if (isSlideVisible) {
            return;
          }

          if (
            slideLeft >=
            slideRect.width
          ) {
            this.slider.slideNext(0);
          } else {
            this.slider.slidePrev(0);
          }
        }
      );

      /*
       * Autoplay
       */
      const swiperOptions =
        JSON.parse(
          this.getAttribute(
            'data-swiper-options'
          )
        ) || {};

      this.slider.wrapperEl.addEventListener(
        'mouseenter',
        () => {
          if (
            this.slider.autoplay &&
            this.slider.autoplay.stop
          ) {
            this.slider.autoplay.stop();
          }
        }
      );

      this.slider.wrapperEl.addEventListener(
        'mouseleave',
        () => {
          if (
            !this.classList.contains(
              'product--open-on-quick-cart'
            ) &&
            this.sliderOptions.autoplay &&
            this.slider.autoplay &&
            this.slider.autoplay.start
          ) {
            this.slider.autoplay.start();
          }

          if (
            swiperOptions.autoplay === false &&
            this.slider.autoplay &&
            this.slider.autoplay.stop
          ) {
            this.slider.autoplay.stop();
          }
        }
      );

      /*
       * Quick cart
       */
      if (
        this.hasAttribute(
          'data-init-quick-cart'
        ) &&
        document.querySelector(
          'quick-cart-drawer'
        ) &&
        this.querySelector(
          '.swiper-slide'
        )
      ) {
        document
          .querySelector(
            'quick-cart-drawer'
          )
          .init();
      }

      /*
       * Product cards
       */
      this.querySelectorAll(
        'product-card'
      ).forEach(card => {
        if (
          typeof card.init ===
          'function'
        ) {
          card.init();
        }
      });

      /*
       * Shopify design mode
       */
      if (Shopify.designMode) {
        if (
          this.hasAttribute(
            'data-init-quick-cart'
          ) &&
          document.querySelector(
            'quick-cart-drawer'
          ) &&
          this.querySelector(
            '.swiper-slide'
          )
        ) {
          document
            .querySelector(
              'quick-cart-drawer'
            )
            .init();
        }
      }
    }
  }

  customElements.define(
    'card-product-slider',
    CardProductSlider
  );
}